import { ComponentProps, ReactNode, useEffect, useState } from 'react';
import { MdAdd, MdOutlineNotificationsActive } from 'react-icons/md';
import { ActivityComment, Event } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { RevealFieldErrors } from '@Components/Forms/FormField';
import { useSetError } from '@src/redux/modals';
import { useNotImplemented, useShowWipActions } from '../activity-feed-utils';
import { useOracleActivity } from '../useOracleActivity';
import { useOracleEventCounts } from '../useOracleEventCounts';
import { CommentList } from './CommentList';
import { CommentsBox } from './CommentsBox';
import { DetailBadgesRow } from './DetailBadgesRow';
import { DetailHeader } from './DetailHeader';
import { DetailInfoCard } from './DetailInfoCard';
import { DetailTextBlock } from './DetailTextBlock';
import { DetailTitleBlock } from './DetailTitleBlock';
import { QuoteCard } from './QuoteCard';
import { isQuoteEvidence } from './activity-feed-detail-utils';
import { useEditEventForm } from './useEditEventForm';

interface ActivityFeedDetailContentProps {
  event: Event;
}

export const ActivityFeedDetailContent = ({ event }: ActivityFeedDetailContentProps): ReactNode => {
  const notImplemented = useNotImplemented();
  const showWip = useShowWipActions();
  const setError = useSetError();
  const quotes = event.evidence.filter(isQuoteEvidence);
  const { activity, error } = useOracleActivity(event.id, event.cid, event.foldedIds);
  // ActivityDetails is typed as an intersection with an optional `comments` field; treat missing
  // (still loading, or upstream omitted) as an empty list so the composer + list-container render
  // straight away and just fill in as the query resolves.
  const comments: ActivityComment[] = activity?.comments ?? [];
  const [isEditing, setIsEditing] = useState(false);
  const bands = useOracleEventCounts()?.bands;

  const { form, isSaving } = useEditEventForm({
    event,
    bands,
    requireDetails: showWip,
    onSaved: () => setIsEditing(false)
  });

  const editor = isEditing ? { form, disabled: isSaving } : undefined;
  const wipEditor = showWip ? editor : undefined;

  const exitEditing = () => {
    form.reset();
    setIsEditing(false);
  };

  useEffect(() => {
    if (error) {
      setError({ message: `Couldn't load activity detail: ${error.message}`, reportError: error });
    }
  }, [error, setError]);

  return (
    <div className='activity-feed-detail'>
      <DetailHeader />
      <div className='activity-feed-detail-body'>
        <DetailBadgesRow
          event={event}
          showWip={showWip}
          isEditing={isEditing}
          onEdit={() => setIsEditing(true)}
        />
        <RevealErrorsWhileEditing form={form} isEditing={isEditing}>
          <DetailTitleBlock event={event} editor={editor} />
          <div className='activity-feed-detail-columns'>
            <div className='activity-feed-detail-columns-main'>
              <DetailTextBlock
                event={event}
                dataKey='details'
                label='Description'
                required
                editor={wipEditor}
              />
              <DetailTextBlock
                event={event}
                dataKey='recommendedAction'
                label='Next Steps'
                editor={wipEditor}
              />
            </div>
            <DetailInfoCard event={event} showWip={showWip} editor={editor} />
          </div>
        </RevealErrorsWhileEditing>
        {quotes.map(quote => (
          <QuoteCard key={quote.id} quote={quote} />
        ))}
        {isEditing && (
          <div className='activity-feed-detail-edit-actions'>
            <Button
              appearance={Appearance.EMPTY}
              design={Design.NEUTRAL}
              disabled={isSaving}
              onClick={exitEditing}
            >
              Cancel
            </Button>
            <form.AppForm>
              <form.SubmitButton requireChanges>Save Edits</form.SubmitButton>
            </form.AppForm>
          </div>
        )}
        <div className='activity-feed-detail-comments-section'>
          <CommentList activityId={event.id} comments={comments} />
          <CommentsBox key={event.id} activityId={event.id} />
        </div>
      </div>
      {showWip && (
        <div className='activity-feed-detail-footer'>
          <Button
            appearance={Appearance.EMPTY}
            onClick={notImplemented}
            leadingElement={<MdOutlineNotificationsActive />}
          >
            Set up alerts
          </Button>
          <Button onClick={notImplemented} leadingElement={<MdAdd />}>
            Create Task
          </Button>
        </div>
      )}
    </div>
  );
};

interface RevealErrorsWhileEditingProps {
  form: ComponentProps<typeof RevealFieldErrors>['form'];
  isEditing: boolean;
  children: ReactNode;
}

// The editor starts from saved data, which can already break a rule (e.g. a blank description
// while WIP is on), so its errors show before any field is touched.
const RevealErrorsWhileEditing = ({
  form,
  isEditing,
  children
}: RevealErrorsWhileEditingProps): ReactNode => {
  if (!isEditing) return children;
  return <RevealFieldErrors form={form}>{children}</RevealFieldErrors>;
};
